import React from 'react';
import {
  energyCarrierNames,
  energyCarrierWeightFactors,
  EnergyConsumer,
  energyConsumerNames,
} from '@shared/helpers/energy/energy-class.model';
import { NodonListItemResult } from '@ui/ListItemResult';
import { getEntries } from '@shared/helpers/object.helpers';
import { entrySortFn } from '@shared/helpers/sort.helpers';
import {
  usePrimaryEnergyValues,
  useYearlyEnergyConsumptionRecord,
} from '@features/energy/energy.hook';
import { multiplyProperties, sum } from '@shared/helpers/math.helpers';
import { isDefined } from '@shared/helpers/array.helpers';
import { toPercent } from '@shared/helpers/string.helpers';
import { getGeographicalAdjustmentFactor } from '@shared/helpers/energy/city-climate.helpers';
import { useBuildingLocation } from '@features/design/hooks/design.hooks';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';

export const PrimaryEnergyValueGroup: React.FC = () => {
  const primaryEnergyValues = usePrimaryEnergyValues();
  const primaryEnergyValue = sum(primaryEnergyValues);
  const energyConsumptionRecord = useYearlyEnergyConsumptionRecord();
  const location = useBuildingLocation();
  const geoFactor = getGeographicalAdjustmentFactor(location);

  const getConsumerTooltip = (consumer: EnergyConsumer): string => {
    const energyMixes = energyConsumptionRecord[consumer];
    const total = sum(energyMixes); // Sum all the energy carriers
    const percentages = multiplyProperties(energyMixes, total ? 1 / total : 0);
    const prefix =
      consumer === EnergyConsumer.Heating
        ? `After geo factor adjustment (÷${getDisplayNumber(geoFactor)}): `
        : '';
    const carriers = getEntries(percentages)
      .map(([carrier, value]) =>
        value
          ? `${energyCarrierNames[carrier]}: ${toPercent(value)} (×${energyCarrierWeightFactors[carrier]})`
          : undefined,
      )
      .filter(isDefined)
      .join('. ');
    const suffix = `. To calculate the primary energy value, the energy consumtion of a certain type is multiplied by a weight factor.`;

    return prefix + carriers + suffix;
  };

  return (
    <NodonListItemResult
      name="Primary energy value"
      tooltip="A measure of the energy performance of the building. It's a geolocation adjusted value, so it's a fair comparison between different geographic locations."
      value={primaryEnergyValue}
      unit="kWh/m^2"
    >
      {/* Primary energy values per consumer */}
      {getEntries(primaryEnergyValues)
        .filter(([, value]) => value)
        .sort(entrySortFn)
        .map(([consumer, value]) =>
          value ? (
            <NodonListItemResult
              key={consumer}
              name={energyConsumerNames[consumer as EnergyConsumer]}
              tooltip={getConsumerTooltip(consumer)}
              value={value}
              unit="kWh/m^2"
              indentation={1}
            />
          ) : null,
        )}
    </NodonListItemResult>
  );
};
